'use es6';

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { FileAttachment } from 'foundations-components/transitional/components/FileAttachment';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { InputStaticLabel } from 'foundations-components/transitional/components/InputStaticLabel';
import { Select } from 'foundations-components/transitional/components/Select';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { ExternalLinkTypes, LinkTypes, ReadOnlyLinkTypes } from '../../constants/link';
import { deserializeAnchorFromLink, serializeAnchorAsLink } from '../../utils/anchor';
import { alphabeticalSort } from '../../utils/link';
import requiredBy from '../../utils/requiredBy';
import { isLikelyUrlWithoutProtocol } from '../../utils/url';
import FormLabelWithHelpLink from '../FormLabelWithHelpLink';
import AnchorLinkField from './AnchorLinkField';
import EmailSubscriptionLinkField from './EmailSubscriptionLinkField';
import MeetingLinkField from './MeetingLinkField';
import PageLinkField from './PageLinkField';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default class LinkFields extends Component {
  constructor(...args) {
    super(...args);
    this.handleLinkTypeChange = ({
      target: {
        value
      }
    }) => {
      this.props.onLinkTypeChange(value);
    };
    this.handleFileSelection = () => {
      const {
        onSelectFile,
        onUpdate
      } = this.props;
      onSelectFile(file => {
        onUpdate({
          href: file.default_hosting_url,
          linkDisplayName: `${file.name}.${file.extension}`,
          fileMeta: {
            fileName: `${file.name}.${file.extension}`,
            size: file.size,
            type: file.type
          }
        });
      });
    };
  }
  renderUrlSearchInput() {
    const {
      href,
      onUpdate,
      linkType,
      shouldAutoFocus,
      isValidLink
    } = this.props;
    return /*#__PURE__*/_jsx(FormControl, {
      label: I18n.text(`RichTextLib.links.linkTypes.${LinkTypes.URL}.label`),
      marginBlockStart: "0",
      error: !isValidLink,
      validationMessage: isValidLink ? '' : I18n.text('RichTextLib.links.invalidLinkMessage'),
      children: /*#__PURE__*/_jsx(TextInput, {
        autoFocus: shouldAutoFocus,
        autoFocusPreventScroll: true,
        "data-test-id": "url-search-input",
        value: linkType === LinkTypes.URL ? href : '',
        onKeyDown: this.props.onUrlKeyDown,
        onChange: ({
          target: {
            value
          }
        }) => {
          // may want to save protocol appending for onRemove so it doesn't just suddenly add it while typing
          const newHref = isLikelyUrlWithoutProtocol(value) ? `http://${value}` : value;
          onUpdate({
            href: newHref,
            linkDisplayName: newHref
          });
        }
      })
    });
  }
  renderEmailInput() {
    const {
      href,
      onUpdate,
      shouldAutoFocus,
      isValidLink
    } = this.props;
    return /*#__PURE__*/_jsx(FormControl, {
      label: I18n.text(`RichTextLib.links.linkTypes.${LinkTypes.EMAIL}.label`),
      marginBlockStart: "0",
      error: !isValidLink,
      validationMessage: isValidLink ? '' : I18n.text('RichTextLib.links.invalidLinkMessage'),
      children: /*#__PURE__*/_jsx(InputStaticLabel, {
        text: 'mailto:',
        position: "start",
        children: /*#__PURE__*/_jsx(TextInput, {
          autoFocus: shouldAutoFocus,
          autoFocusPreventScroll: true,
          onKeyDown: this.props.onUrlKeyDown,
          "data-test-id": "email-address-input",
          value: href.replace(/^mailto:/, ''),
          onChange: ({
            target: {
              value
            }
          }) => {
            const newValue = value.replace(/^mailto:/, '');
            const newHref = `mailto:${newValue}`;
            onUpdate({
              href: newHref,
              linkDisplayName: newValue
            });
          }
        })
      })
    });
  }
  renderPageSelection() {
    const {
      href,
      onUpdate,
      linkType,
      dropdownClassName,
      fetchPages,
      shouldAutoFocus
    } = this.props;
    return /*#__PURE__*/_jsx(PageLinkField, {
      autoFocus: shouldAutoFocus,
      url: linkType === LinkTypes.PAGE ? href : '',
      fetchPages: fetchPages,
      dropdownClassName: dropdownClassName,
      onChange: ({
        url,
        pageName
      }) => {
        onUpdate({
          href: url,
          linkDisplayName: pageName
        });
      }
    });
  }
  renderFileSelection() {
    const {
      currentFileMeta
    } = this.props;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(FormLabelWithHelpLink, {
        primaryText: `RichTextLib.links.linkTypes.${LinkTypes.FILE}.label`,
        linkText: currentFileMeta ? `RichTextLib.links.linkTypes.${LinkTypes.FILE}.replace` : null,
        onLinkClick: this.handleFileSelection,
        "data-test-id": "link-file-replace"
      }), currentFileMeta ? /*#__PURE__*/_jsx(FileAttachment, {
        "data-test-id": "link-file-attachment",
        size: "small",
        onCloseClick: () => this.props.onUpdate({
          fileMeta: null,
          href: ''
        }),
        fileName: currentFileMeta.fileName,
        fileInfo: I18n.formatSize(currentFileMeta.size),
        fileType: currentFileMeta.type,
        closeable: true
      }) : /*#__PURE__*/_jsx(Button, {
        "data-test-id": "link-select-file",
        size: "small",
        use: "tertiary-light",
        onClick: this.handleFileSelection,
        children: I18n.text('RichTextLib.links.selectFile')
      })]
    });
  }
  renderEmailSubscriptionSelection() {
    const {
      href,
      onUpdate,
      linkType,
      dropdownClassName,
      emailSubscriptionLinkTypes
    } = this.props;
    return /*#__PURE__*/_jsx(EmailSubscriptionLinkField, {
      onUpdate: onUpdate,
      href: href,
      linkType: linkType,
      dropdownClassName: dropdownClassName,
      emailSubscriptionLinkTypes: emailSubscriptionLinkTypes
    });
  }
  renderMeetingSelection() {
    const {
      userId,
      href,
      fetchMeetingsByUsers,
      dropdownClassName,
      onUpdate
    } = this.props;
    return /*#__PURE__*/_jsx(MeetingLinkField, {
      userId: userId,
      url: href,
      fetchMeetingsByUsers: fetchMeetingsByUsers,
      dropdownClassName: dropdownClassName,
      onChange: ({
        url,
        meetingName
      }) => onUpdate({
        href: url,
        linkDisplayName: meetingName
      })
    });
  }
  renderAnchorInput() {
    const {
      href,
      fetchAnchors,
      onUpdate,
      dropdownClassName
    } = this.props;
    return /*#__PURE__*/_jsx(AnchorLinkField, {
      anchor: deserializeAnchorFromLink(href),
      fetchAnchors: fetchAnchors,
      dropdownClassName: dropdownClassName,
      onChange: ({
        anchorName
      }) => {
        onUpdate({
          href: serializeAnchorAsLink(anchorName),
          linkDisplayName: anchorName
        });
      }
    });
  }
  renderNoLinkInput() {
    return /*#__PURE__*/_jsx(FormControl, {
      label: I18n.text('RichTextLib.links.linkTypes.no_link.label'),
      marginBlockStart: "0",
      children: /*#__PURE__*/_jsx(TextInput, {
        disabled: true,
        value: I18n.text('RichTextLib.links.linkTypes.no_link.placeholder')
      })
    });
  }
  renderUrlField() {
    const {
      linkType
    } = this.props;
    switch (linkType) {
      case LinkTypes.URL:
        return this.renderUrlSearchInput();
      case LinkTypes.PAGE:
        return this.renderPageSelection();
      case LinkTypes.EMAIL:
        return this.renderEmailInput();
      case LinkTypes.FILE:
        return this.renderFileSelection();
      case LinkTypes.EMAIL_SUBSCRIPTION:
        return this.renderEmailSubscriptionSelection();
      case LinkTypes.MEETING:
        return this.renderMeetingSelection();
      case LinkTypes.ANCHOR:
        return this.renderAnchorInput();
      case LinkTypes.NONE:
        return this.renderNoLinkInput();
      default:
        return null;
    }
  }
  renderLinkSelection() {
    const {
      linkType,
      useEmailSubscription,
      allowFileDownload,
      allowMeetingLink,
      allowAnchorLinks,
      useNoLinkOption,
      allowEmailWebVersionLink
    } = this.props;
    const linkTypeOptions = [{
      text: I18n.text(`RichTextLib.links.linkTypes.${LinkTypes.EMAIL}.option`),
      value: LinkTypes.EMAIL
    }, {
      text: I18n.text(`RichTextLib.links.linkTypes.${LinkTypes.URL}.option`),
      value: LinkTypes.URL
    }, {
      text: I18n.text(`RichTextLib.links.linkTypes.${LinkTypes.PAGE}.option`),
      value: LinkTypes.PAGE
    }];
    if (allowEmailWebVersionLink) {
      linkTypeOptions.push({
        text: I18n.text(`RichTextLib.links.linkTypes.${LinkTypes.VIEW_AS_WEBPAGE}.option`),
        value: LinkTypes.VIEW_AS_WEBPAGE
      });
    }
    if (allowFileDownload) {
      linkTypeOptions.push({
        text: I18n.text(`RichTextLib.links.linkTypes.${LinkTypes.FILE}.option`),
        value: LinkTypes.FILE
      });
    }
    if (useEmailSubscription) {
      linkTypeOptions.push({
        text: I18n.text(`RichTextLib.links.linkTypes.${LinkTypes.EMAIL_SUBSCRIPTION}.option`),
        value: LinkTypes.EMAIL_SUBSCRIPTION
      });
    }
    if (allowMeetingLink) {
      linkTypeOptions.push({
        text: I18n.text(`RichTextLib.links.linkTypes.${LinkTypes.MEETING}.option`),
        value: LinkTypes.MEETING
      });
    }
    if (allowAnchorLinks) {
      linkTypeOptions.push({
        text: I18n.text(`RichTextLib.links.linkTypes.${LinkTypes.ANCHOR}.option`),
        value: LinkTypes.ANCHOR
      });
    }

    // re-order alphabetically based on option label
    linkTypeOptions.sort(alphabeticalSort);

    // place no-link at the top since it's the default zero-state
    if (useNoLinkOption) {
      linkTypeOptions.unshift({
        text: I18n.text(`RichTextLib.links.linkTypes.${LinkTypes.NONE}.option`),
        value: LinkTypes.NONE
      });
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(FormControl, {
        marginBlockEnd: "100",
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "RichTextLib.links.linkTo"
        }),
        children: /*#__PURE__*/_jsx(Select, {
          menuStyle: {
            maxHeight: 300
          },
          value: linkType,
          placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "RichTextLib.links.linkTypes.placeholder"
          }),
          options: linkTypeOptions,
          defaultValue: "url",
          searchable: false,
          "data-test-id": "link-type-select",
          onChange: this.handleLinkTypeChange
        })
      }), this.renderUrlField()]
    });
  }
  renderCheckboxes() {
    const {
      externalLink,
      linkType,
      showRefAndTargetControls,
      onUpdate
    } = this.props;
    if (showRefAndTargetControls) {
      const handleChange = ({
        target: {
          checked
        }
      }) => {
        onUpdate({
          externalLink: checked
        });
      };
      let readOnly = ReadOnlyLinkTypes.includes(linkType);
      const shouldBeExternal = ExternalLinkTypes.includes(linkType);
      if (readOnly && shouldBeExternal !== externalLink) {
        // This allow users to select the suggested option on legacy links
        readOnly = false;
      }
      return /*#__PURE__*/_jsx(Checkbox, {
        marginBlockStart: "300",
        size: "small",
        checked: externalLink || linkType === LinkTypes.EMAIL,
        readOnly: readOnly,
        disabled: linkType === LinkTypes.NONE,
        onChange: handleChange,
        children: /*#__PURE__*/_jsx(Tooltip, {
          title: I18n.text('RichTextLib.links.openNewWindowReadOnly', {
            linkType
          }),
          placement: "top",
          disabled: !readOnly,
          onMouseDown: evt => evt.preventDefault(),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "RichTextLib.links.openNewWindow"
          })
        })
      });
    }
    return null;
  }
  render() {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [this.renderLinkSelection(), this.renderCheckboxes()]
    });
  }
}
LinkFields.defaultProps = {
  useEmailSubscription: false,
  allowFileDownload: true,
  allowAnchorLinks: false,
  useNoLinkOption: false,
  shouldAutoFocus: true,
  isValidLink: true,
  allowEmailWebVersionLink: false
};
LinkFields.propTypes = {
  externalLink: PropTypes.bool.isRequired,
  href: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onLinkTypeChange: PropTypes.func.isRequired,
  fetchPages: PropTypes.func.isRequired,
  fetchMeetingsByUsers: requiredBy('allowMeetingLink', PropTypes.func),
  fetchAnchors: requiredBy('allowAnchorLinks', PropTypes.func),
  linkType: PropTypes.oneOf(Object.values(LinkTypes)),
  onSelectFile: requiredBy('allowFileDownload', PropTypes.func),
  onUrlKeyDown: PropTypes.func.isRequired,
  currentFileMeta: PropTypes.object,
  dropdownClassName: PropTypes.string,
  showRefAndTargetControls: PropTypes.bool,
  useEmailSubscription: PropTypes.bool,
  allowFileDownload: PropTypes.bool,
  allowMeetingLink: PropTypes.bool,
  emailSubscriptionLinkTypes: PropTypes.array,
  allowAnchorLinks: PropTypes.bool,
  useNoLinkOption: PropTypes.bool,
  shouldAutoFocus: PropTypes.bool,
  userId: PropTypes.number,
  isValidLink: PropTypes.bool,
  allowEmailWebVersionLink: PropTypes.bool
};